<template>
    <div
        v-if="hasFilters || typeFilterOptions && typeFilterOptions.length > 0"
        :class="{ 'bsdc:opacity-50 bsdc:pointer-events-none': loading }"
    >
        <div class="bsdc:flex bsdc:flex-col bsdc:gap-6">
            <div class="bsdc:grid bsdc:grid-cols-1 bsdc:lg:grid-cols-2 bsdc:gap-7.5">
                <RadioFilter
                    v-if="typeFilterOptions && typeFilterOptions.length > 0"
                    label="Stone Type"
                    name="type"
                    :options="typeFilterOptions"
                    v-model:selectedOption="selectedType"
                    @change="handleTypeFilterChange"
                >
                    <template
                        v-if="tooltipModals.type.content"
                        #label
                    >
                        <button
                            class="bsdc-tooltip bsdc:cursor-pointer!"
                            @click.prevent="tooltipModals.type.active = true"
                        >?</button>
                    </template>
                </RadioFilter>

                <RangeFilter
                    label="Carat Weight"
                    name="carat"
                    v-model:range="caratFilterRange"
                    :min="0"
                    :max="10.0"
                    :step="caratStep"
                    @update:range="handleCaratFilterChange"
                >
                    <template
                        v-if="tooltipModals.carat.content"
                        #label
                    >
                        <button
                            class="bsdc-tooltip bsdc:cursor-pointer!"
                            @click.prevent="tooltipModals.carat.active = true"
                        >?</button>
                    </template>
                </RangeFilter>

                <CheckboxFilter
                    v-if="enabledFilters.includes('color') && filters.color"
                    label="Colour"
                    name="color"
                    :options="filters.color.found"
                    :colored="!stones.includes('diamond')"
                    v-model:selectedOptions="filters.color.selected"
                    @change="handleFilterChange"
                    @update:selectedOptions="handleFilterChange"
                >
                    <template
                        v-if="tooltipModals.color.content"
                        #label
                    >
                        <button
                            class="bsdc-tooltip bsdc:cursor-pointer!"
                            @click.prevent="tooltipModals.color.active = true"
                        >?</button>
                    </template>
                </CheckboxFilter>

                <CheckboxFilter
                    v-if="enabledFilters.includes('clarity') && filters.clarity"
                    label="Clarity"
                    name="clarity"
                    :options="filters.clarity.found"
                    v-model:selectedOptions="filters.clarity.selected"
                    @change="handleFilterChange"
                    @update:selectedOptions="handleFilterChange"
                >
                    <template
                        v-if="tooltipModals.clarity.content"
                        #label
                    >
                        <button
                            class="bsdc-tooltip bsdc:cursor-pointer!"
                            @click.prevent="tooltipModals.clarity.active = true"
                        >?</button>
                    </template>
                </CheckboxFilter>
            </div>
        </div>

        <div
            v-if="hasAdvancedFilters"
            class="bsdc:border-y bsdc:border-2-1 bsdc:flex bsdc:flex-col bsdc:gap-5 bsdc:py-5 bsdc:mt-5"
        >
            <button
                class="bsdc:flex bsdc:font-semibold bsdc:text-[13px] bsdc:justify-between"
                @click.prevent="advancedFiltersActive = !advancedFiltersActive"
            >
                Advanced Filters
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="4.5"
                    viewBox="0 0 8 4.5"
                    fill="currentColor"
                    class="bsdc:w-[9px] bsdc:transition-transform"
                    :class="{
                        'bsdc:rotate-180': advancedFiltersActive,
                    }"
                >
                    <path
                        d="M0,.5C0,.37207.04883.24414.14648.14648.3418-.04883.6582-.04883.85352.14648l3.14648,3.14648L7.14648.14648c.19531-.19531.51172-.19531.70703,0s.19531.51172,0,.70703l-3.5,3.5c-.19531.19531-.51172.19531-.70703,0L.14648.85352c-.09766-.09766-.14648-.22559-.14648-.35352Z"
                    />
                </svg>
            </button>

            <div
                class="bsdc:grid bsdc:grid-cols-1 bsdc:lg:grid-cols-6 bsdc:gap-x-7.5 bsdc:gap-y-6"
                :class="{
                    'bsdc:hidden': !advancedFiltersActive,
                }"
            >
                <RangeFilter
                    v-if="enabledFilters.includes('price') && filters.price && filters.price.min !== filters.price.max"
                    class="bsdc:lg:col-span-6"
                    label="Price"
                    name="price"
                    tooltipPrefix="€"
                    v-model:range="filters.price.range"
                    :min="filters.price.min"
                    :max="filters.price.max"
                    :step="filters.price.step"
                    @update:range="handleFilterChange"
                    @input="handleFilterChange"
                >
                    <template
                        v-if="tooltipModals.price.content"
                        #label
                    >
                        <button
                            class="bsdc-tooltip bsdc:cursor-pointer!"
                            @click.prevent="tooltipModals.price.active = true"
                        >?</button>
                    </template>
                </RangeFilter>

                <div
                    class="bsdc:grid bsdc:grid-cols-1 bsdc:lg:grid-cols-6 bsdc:gap-x-7.5 bsdc:gap-y-6 bsdc:lg:col-span-6">
                    <RadioFilter
                        v-if="enabledFilters.includes('shape') && filters.shape"
                        class="bsdc:lg:col-span-3"
                        label="Shape"
                        name="shape"
                        iconPrefix="shape-"
                        :options="filters.shape.found"
                        v-model:selectedOption="filters.shape.selected"
                        @change="handleFilterChange"
                    >
                        <template
                            v-if="tooltipModals.shape.content"
                            #label
                        >
                            <button
                                class="bsdc-tooltip bsdc:cursor-pointer!"
                                @click.prevent="tooltipModals.shape.active = true"
                            >?</button>
                        </template>
                    </RadioFilter>

                    <CheckboxFilter
                        v-if="enabledFilters.includes('cut') && filters.cut"
                        class="bsdc:lg:col-span-3"
                        label="Cut"
                        name="cut"
                        :options="filters.cut.found"
                        v-model:selectedOptions="filters.cut.selected"
                        @change="handleFilterChange"
                        @update:selectedOptions="handleFilterChange"
                    >
                        <template
                            v-if="tooltipModals.cut.content"
                            #label
                        >
                            <button
                                class="bsdc-tooltip bsdc:cursor-pointer!"
                                @click.prevent="tooltipModals.cut.active = true"
                            >?</button>
                        </template>
                    </CheckboxFilter>

                    <CheckboxFilter
                        v-if="enabledFilters.includes('availability') && filters.brand && filters.brand.found.length > 1"
                        class="bsdc:lg:col-span-3"
                        label="Availability"
                        name="availability"
                        :options="filters.brand.found"
                        v-model:selectedOptions="filters.brand.selected"
                        @change="handleFilterChange"
                        @update:selectedOptions="handleFilterChange"
                    >
                        <template
                            v-if="tooltipModals.availability.content"
                            #label
                        >
                            <button
                                class="bsdc-tooltip bsdc:cursor-pointer!"
                                @click.prevent="tooltipModals.availability.active = true"
                            >?</button>
                        </template>
                    </CheckboxFilter>

                    <CheckboxFilter
                        v-if="enabledFilters.includes('certificate_body') && filters.certificate_body"
                        class="bsdc:lg:col-span-3"
                        label="Certificate Body"
                        name="certificate_body"
                        :options="filters.certificate_body.found"
                        v-model:selectedOptions="filters.certificate_body.selected"
                        @change="handleFilterChange"
                        @update:selectedOptions="handleFilterChange"
                    >
                        <template
                            v-if="tooltipModals.certificateBody.content"
                            #label
                        >
                            <button
                                class="bsdc-tooltip bsdc:cursor-pointer!"
                                @click.prevent="tooltipModals.certificateBody.active = true"
                            >?</button>
                        </template>
                    </CheckboxFilter>

                    <CheckboxFilter
                        v-if="enabledFilters.includes('polish') && filters.polish"
                        class="bsdc:lg:col-span-3"
                        label="Polish"
                        name="polish"
                        :options="filters.polish.found"
                        v-model:selectedOptions="filters.polish.selected"
                        @change="handleFilterChange"
                        @update:selectedOptions="handleFilterChange"
                    >
                        <template
                            v-if="tooltipModals.polish.content"
                            #label
                        >
                            <button
                                class="bsdc-tooltip bsdc:cursor-pointer!"
                                @click.prevent="tooltipModals.polish.active = true"
                            >?</button>
                        </template>
                    </CheckboxFilter>

                    <CheckboxFilter
                        v-if="enabledFilters.includes('symmetry') && filters.symmetry"
                        class="bsdc:lg:col-span-3"
                        label="Symmetry"
                        name="symmetry"
                        :options="filters.symmetry.found"
                        v-model:selectedOptions="filters.symmetry.selected"
                        @change="handleFilterChange"
                        @update:selectedOptions="handleFilterChange"
                    >
                        <template
                            v-if="tooltipModals.symmetry.content"
                            #label
                        >
                            <button
                                class="bsdc-tooltip bsdc:cursor-pointer!"
                                @click.prevent="tooltipModals.symmetry.active = true"
                            >?</button>
                        </template>
                    </CheckboxFilter>

                    <CheckboxFilter
                        v-if="enabledFilters.includes('fluorescence') && filters.fluorescence"
                        class="bsdc:lg:col-span-3"
                        label="Fluorescence"
                        name="fluorescence"
                        :options="filters.fluorescence.found"
                        v-model:selectedOptions="filters.fluorescence.selected"
                        @change="handleFilterChange"
                        @update:selectedOptions="handleFilterChange"
                    >
                        <template
                            v-if="tooltipModals.fluorescence.content"
                            #label
                        >
                            <button
                                class="bsdc-tooltip bsdc:cursor-pointer!"
                                @click.prevent="tooltipModals.fluorescence.active = true"
                            >?</button>
                        </template>
                    </CheckboxFilter>
                </div>

                <RangeFilter
                    v-if="enabledFilters.includes('table') && filters.table"
                    class="bsdc:lg:col-span-2"
                    label="Table %"
                    name="table"
                    v-model:range="filters.table.range"
                    :min="filters.table.min"
                    :max="filters.table.max"
                    :step="filters.table.step"
                    @update:range="handleFilterChange"
                    @input="handleFilterChange"
                >
                    <template
                        v-if="tooltipModals.table.content"
                        #label
                    >
                        <button
                            class="bsdc-tooltip bsdc:cursor-pointer!"
                            @click.prevent="tooltipModals.table.active = true"
                        >?</button>
                    </template>
                </RangeFilter>

                <RangeFilter
                    v-if="enabledFilters.includes('depth') && filters.depth"
                    class="bsdc:lg:col-span-2"
                    label="Depth %"
                    name="depth"
                    v-model:range="filters.depth.range"
                    :min="filters.depth.min"
                    :max="filters.depth.max"
                    :step="filters.depth.step"
                    @update:range="handleFilterChange"
                    @input="handleFilterChange"
                >
                    <template #label>
                        <button
                            class="bsdc-tooltip bsdc:cursor-pointer!"
                            @click.prevent="tooltipModals.depth.active = true"
                        >?</button>
                    </template>
                </RangeFilter>

                <RangeFilter
                    v-if="enabledFilters.includes('ratio') && filters.ratio"
                    class="bsdc:lg:col-span-2"
                    label="Ratio"
                    name="ratio"
                    v-model:range="filters.ratio.range"
                    :min="filters.ratio.min"
                    :max="filters.ratio.max"
                    :step="filters.ratio.step"
                    @update:range="handleFilterChange"
                    @input="handleFilterChange"
                >
                    <template #label>
                        <button
                            class="bsdc-tooltip bsdc:cursor-pointer!"
                            @click.prevent="tooltipModals.ratio.active = true"
                        >?</button>
                    </template>
                </RangeFilter>
            </div>
        </div>

        <div class="bsdc:mt-6">
            <button
                type="button"
                class="button button-full"
                :disabled="!filtersDirty || loading"
                @click="handleApplyFilters"
            >
                APPLY FILTERS
            </button>
        </div>
    </div>

    <Modal
        v-for="modal in tooltipModals"
        :key="modal.title"
        :title="modal.title"
        :active="modal.active"
        :version="2"
        @close="modal.active = false"
    >
        <div class="bsdc:text-[13px]">
            <p v-html="modal.content"></p>
        </div>
    </Modal>
</template>

<script setup>
    import { ref, computed, watch, onMounted } from "vue";

    import RadioFilter from "@js/Components/RadioFilter.vue";
    import RangeFilter from "@js/Components/RangeFilter.vue";
    import CheckboxFilter from "@js/Components/CheckboxFilter.vue";
    import Modal from "@js/Components/Modal.vue";

    const emits = defineEmits([
        'loaded',
        'changed',
        'applyFilters',
    ]);

    const props = defineProps({
        httpExternal: Function,
        configuratorSettings: Object,
        enabledFilters: {
            type: Array,
            default: () => [],
        },
        caratMin: {
            type: Number,
            default: 0,
        },
        caratMax: {
            type: Number,
            default: 0,
        },
        caratRange: {
            type: Array,
            default: () => [0, 0],
        },
        caratDefaultMin: {
            type: Number,
            default: 0.30,
        },
        caratDefaultMax: {
            type: Number,
            default: 10.0,
        },
        caratStep: {
            type: Number,
            default: 0.05,
        },
        caratTooltipContent: {
            type: String,
            default: '',
        },
        typeFilterOptions: {
            type: Array,
            default: () => [],
        },
        selectedTypeValue: {
            type: String,
            default: () => 'lab',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        filtersDirty: {
            type: Boolean,
            default: false,
        },
    });

    const stones = computed(() => props.configuratorSettings.stones);
    const allowedFilters = computed(() => props.configuratorSettings.filters);

    const advancedFiltersActive = ref(false);

    const caratFilterRange = ref([props.caratDefaultMin, props.caratDefaultMax]);
    const selectedType = ref(props.selectedTypeValue || 'lab');

    const filterTypes = ref({
        radio: ['shape'],
        range: ['price', 'table', 'depth', 'ratio'],
    });

    const filters = ref(
        Object.keys(allowedFilters.value || {}).reduce((acc, key) => {
            const type = Object.keys(filterTypes.value).find(type => filterTypes.value[type].includes(key)) || 'checkbox';

            let data = {};

            switch (type) {
                case 'radio':
                case 'checkbox':
                    data = {
                        allowed: allowedFilters.value[key].length > 0 ? allowedFilters.value[key] : [-1],
                        found: [],
                        selected: null,
                    };
                    break;
                case 'range': {
                    const min = allowedFilters.value[key]?.min || 0;
                    const max = allowedFilters.value[key]?.max || 0;

                    data = {
                        allowed: [min, max],
                        min: min,
                        max: max,
                        range: [min, max],
                        step: 0.05,
                    };
                    break;
                }
                default:
                    break;
            }

            acc[key] = {
                value: key,
                type: type,
                ...data,
            };
            return acc;
        }, {})
    );

    const hasFilters = computed(() => {
        return Object.values(filters.value).some(filter => Array.isArray(filter.allowed) && filter.allowed.length > 0);
    });

    const hasAdvancedFilters = computed(() => {
        const checkboxKeys = ['shape', 'cut', 'certificate_body', 'polish', 'symmetry', 'fluorescence'];
        const rangeKeys = ['table', 'depth', 'ratio'];

        const hasShapeFilter = props.enabledFilters.includes('shape') && filters.value.shape;
        const hasCutFilter = props.enabledFilters.includes('cut') && filters.value.cut;
        const hasAvailabilityFilter = props.enabledFilters.includes('availability') && filters.value.brand;
        const hasCheckboxFilters = checkboxKeys.some(key => allowedFilters.value[key] && allowedFilters.value[key].length > 0);
        const hasRangeFilters = Object.values(filters.value).some(filter => filter.type === 'range' && filter.min !== filter.max && rangeKeys.includes(filter.value));

        return hasShapeFilter || hasCutFilter || hasAvailabilityFilter || hasCheckboxFilters || hasRangeFilters;
    });

    const selected = computed(() => {
        const result = Object.entries(filters.value).reduce((acc, [key, filter]) => {
            if (filter.selected) {
                acc[key] = Array.isArray(filter.selected) ? filter.selected : [filter.selected];
            }

            if (filter?.range) {
                acc[key] = {
                    min: filter.range[0],
                    max: filter.range[1],
                };
            }

            return acc;
        }, {});

        if (caratFilterRange.value.length === 2) {
            result.weight = {
                min: caratFilterRange.value[0],
                max: caratFilterRange.value[1],
            };
        }

        if (selectedType.value) {
            result.type = [selectedType.value];
        }

        return result;
    });

    const tooltipModals = ref({
        type: {
            title: 'Stone Type',
            content: props.configuratorSettings?.filterTooltips?.type || '',
            active: false,
        },
        carat: {
            title: 'Carat',
            content: props.caratTooltipContent,
            active: false,
        },
        shape: {
            title: 'Shape',
            content: props.configuratorSettings.filterTooltips.shape,
            active: false,
        },
        price: {
            title: 'Price',
            content: props.configuratorSettings.filterTooltips.price,
            active: false,
        },
        color: {
            title: 'Color',
            content: props.configuratorSettings.filterTooltips.color,
            active: false,
        },
        clarity: {
            title: 'Clarity',
            content: props.configuratorSettings.filterTooltips.clarity,
            active: false,
        },
        cut: {
            title: 'Cut',
            content: props.configuratorSettings.filterTooltips.cut,
            active: false,
        },
        certificateBody: {
            title: 'Certificate Body',
            content: props.configuratorSettings.filterTooltips.certificate_body,
            active: false,
        },
        availability: {
            title: 'Availability',
            content: props.configuratorSettings.filterTooltips.availability,
            active: false,
        },
        polish: {
            title: 'Polish',
            content: props.configuratorSettings.filterTooltips.polish,
            active: false,
        },
        symmetry: {
            title: 'Symmetry',
            content: props.configuratorSettings.filterTooltips.symmetry,
            active: false,
        },
        fluorescence: {
            title: 'Fluorescence',
            content: props.configuratorSettings.filterTooltips.fluorescence,
            active: false,
        },
        table: {
            title: 'Table',
            content: props.configuratorSettings.filterTooltips.table,
            active: false,
        },
        depth: {
            title: 'Depth',
            content: props.configuratorSettings.filterTooltips.depth,
            active: false,
        },
        ratio: {
            title: 'Ratio',
            content: props.configuratorSettings.filterTooltips.ratio,
            active: false,
        },
    });

    const sortFilterOptionsByAllowedOrder = (filterOptions, allowedOrder) => {
        if (!Array.isArray(filterOptions)) {
            return filterOptions;
        }

        // If allowedOrder contains -1, it means all options are allowed (no filtering)
        if (allowedOrder.includes(-1)) {
            return filterOptions;
        }

        // Filter to only include options that are in the allowed list, then sort
        return filterOptions
            .filter(option => {
                const value = option.value || option;
                return allowedOrder.includes(value);
            })
            .sort((a, b) => {
                const aValue = a.value || a;
                const bValue = b.value || b;
                const aIndex = allowedOrder.indexOf(aValue);
                const bIndex = allowedOrder.indexOf(bValue);

                return aIndex - bIndex;
            });
    };

    const filterOverrides = ref({
        custom: props.configuratorSettings.filter_overrides || {},
    });

    const getDefaultSelectedValues = (filterKey, requestedFilter) => {
        // Check if we have default selections for this filter
        const defaultSelections = props.configuratorSettings?.filters?.[`${filterKey}_selected`];

        // If no default selections configured, use current behavior (select all found)
        if (!defaultSelections || !Array.isArray(defaultSelections) || defaultSelections.length === 0) {
            return requestedFilter.found.map(option => option.value || option);
        }

        // Get array of found values for validation
        const foundValues = requestedFilter.found.map(option => option.value || option);

        // Filter default selections to only include values that are actually found/available
        const validSelections = defaultSelections.filter(value => foundValues.includes(value));

        // If none of the default selections are valid/available, fall back to selecting all
        if (validSelections.length === 0) {
            return requestedFilter.found.map(option => option.value || option);
        }

        return validSelections;
    };

    const loadFilters = async () => {
        if (!props.httpExternal) {
            throw new Error('httpExternal client not initialized');
        }

        const filterParams = {};

        Object.entries(filters.value).forEach(([key, filter]) => {
            filterParams[key] = filter.allowed;
        });

        const response = await props.httpExternal.get('/diamonds/filters', {
            params: {
                ...filterParams,
                ...filterOverrides.value,
            },
        });

        // Map each filter response to the local filter state
        Object.entries(response.data).forEach(([key, filter]) => {
            const requestedFilter = filters.value?.[key];

            if (requestedFilter) {
                if (requestedFilter.type === 'radio' || requestedFilter.type === 'checkbox') {
                    if (Array.isArray(filter)) {
                        requestedFilter.found = sortFilterOptionsByAllowedOrder(filter, requestedFilter.allowed);
                    }
                }

                if (requestedFilter.type === 'radio') {
                    requestedFilter.selected = requestedFilter.found[0]?.value;
                } else if (requestedFilter.type === 'range') {
                    //
                } else {
                    requestedFilter.selected = getDefaultSelectedValues(key, requestedFilter);
                }
            }
        });
    };

    const loadFiltersAndEmit = async () => {
        if (!props.httpExternal) {
            console.warn('httpExternal not available yet, waiting...');
            return;
        }

        await loadFilters()
            .then(() => {
                emits('changed', selected.value);
                emits('loaded', true);
            })
            .catch((error) => {
                console.error('Failed to load filters:', error);
                emits('loaded', false);
            });
    };

    watch([() => props.caratDefaultMin, () => props.caratDefaultMax], ([newMin, newMax]) => {
        if (newMin !== undefined && newMax !== undefined && newMin !== newMax) {
            caratFilterRange.value = [newMin, newMax];
        }
    }, { immediate: true });

    watch(() => props.selectedTypeValue, (newValue) => {
        if (newValue) {
            selectedType.value = newValue;
        }
    }, { immediate: true });

    const handleFilterChange = () => {
        emits('changed', selected.value);
    };

    const handleTypeFilterChange = handleFilterChange;

    const handleCaratFilterChange = handleFilterChange;

    const handleApplyFilters = () => {
        emits('applyFilters');
    };

    onMounted(async () => {
        await loadFiltersAndEmit();

        $(document).on("show_variation", loadFiltersAndEmit);
    });
</script>

<style>
    @reference "../../css/app.css";

    .bsdc-config-filters--advanced {
        @apply bsdc:bg-2-1 bsdc:rounded-md;
    }

    .bsdc-config-filters__label {
        @apply bsdc:mb-3 bsdc:gap-2 bsdc:flex bsdc:items-center bsdc:text-[13px] bsdc:font-semibold;
    }

    .bsdc-config-selector {
        @apply bsdc:flex bsdc:flex-wrap bsdc:gap-0.5;
    }

    .bsdc-config-selector__item {
        @apply bsdc:py-3 bsdc:px-4 bsdc:flex bsdc:items-center bsdc:justify-center bsdc:gap-1.5 bsdc:cursor-pointer bsdc:text-center bsdc:border bsdc:border-1-1/20 bsdc:text-[13px] bsdc:rounded-md bsdc:leading-none bsdc:relative;

        img {
            @apply bsdc:-mt-[1.5px];
        }

        input,
        .jcf-radio,
        .jcf-checkbox {
            @apply bsdc:hidden;
        }
    }

    .bsdc-config-selector__item.active {
        @apply bsdc:bg-neutral-800 bsdc:text-white bsdc:border-neutral-800;

        &::before {
            @apply bsdc:size-3 bsdc:-mt-0.5 bsdc:rounded-full bsdc:bg-white bsdc:content-[''];
        }

        img {
            @apply bsdc:invert;
        }
    }

    .bsdc-config-selector__item:not(.bsdc-config-selector__item--color).active {
        &::before {
            @apply bsdc:bg-[image:var(--icon-check)] bsdc:bg-no-repeat bsdc:bg-center bsdc:bg-[length:8px];
        }
    }

    .bsdc-config-selector__item--color {
        @apply bsdc:relative;

        &::before {
            @apply bsdc:size-[10px] bsdc:rounded-full bsdc:border bsdc:border-solid bsdc:border-neutral-800;
            content: '';
        }

        &.bsdc-config-selector__item--blue::before {
            @apply bsdc:bg-blue-500;
        }

        &.bsdc-config-selector__item--green::before {
            @apply bsdc:bg-green-500;
        }

        &.bsdc-config-selector__item--red::before {
            @apply bsdc:bg-red-500;
        }

        &.bsdc-config-selector__item--teal::before {
            @apply bsdc:bg-teal-500;
        }

        &.bsdc-config-selector__item--pink::before {
            @apply bsdc:bg-pink-500;
        }

        &.bsdc-config-selector__item--purple::before {
            @apply bsdc:bg-purple-500;
        }

        &.bsdc-config-selector__item--yellow::before {
            @apply bsdc:bg-yellow-500;
        }

        &.bsdc-config-selector__item--violet::before {
            @apply bsdc:bg-violet-500;
        }

        &.bsdc-config-selector__item--brown::before {
            @apply bsdc:bg-amber-900;
        }

        &.bsdc-config-selector__item--orange::before {
            @apply bsdc:bg-orange-500;
        }

        &.bsdc-config-selector__item--grey::before {
            @apply bsdc:bg-gray-600;
        }

        &.bsdc-config-selector__item--white::before {
            @apply bsdc:bg-white;
        }

        &.bsdc-config-selector__item--black::before {
            @apply bsdc:bg-black;
        }

        &.bsdc-config-selector__item--other::before {
            @apply bsdc:bg-gray-300;
        }
    }

    .bsdc-config-selector__item--color.active {
        &::before {
            @apply bsdc:border-neutral-200;
        }
    }
</style>
